import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getCoachee, getSessions, deleteSession } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Edit, 
  Plus, 
  User, 
  Mail, 
  Phone, 
  Calendar, 
  Briefcase,
  Target,
  MessageSquare,
  ClipboardList,
  Loader2,
  Eye,
  Trash2,
  FileText
} from 'lucide-react';

export default function CoacheeDetail() {
  const { coacheeId } = useParams();
  const navigate = useNavigate();
  const [coachee, setCoachee] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [coacheeId]);

  const loadData = async () => {
    try {
      const [coacheeData, sessionsData] = await Promise.all([
        getCoachee(coacheeId),
        getSessions(coacheeId)
      ]);
      setCoachee(coacheeData);
      setSessions(sessionsData);
    } catch (error) {
      toast.error('Error al cargar datos del coachee');
      navigate('/coachees');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteSession(coacheeId, sessionId);
      toast.success('Sesión eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar sesión');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-10 h-10 animate-spin text-blue-900" />
        </div>
      </Layout>
    );
  }

  if (!coachee) {
    return null;
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="coachee-detail-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/coachees')}
              className="p-2 hover:bg-slate-100 rounded-full mt-1"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-900 to-sky-500 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                {coachee.nombre?.charAt(0).toUpperCase()}
              </div>
              <div>
                <h1 className="text-heading text-3xl text-slate-800">
                  {coachee.nombre}
                </h1>
                <p className="text-slate-500">
                  Coachee desde {coachee.fecha_creacion || 'N/A'}
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2 ml-14 sm:ml-0">
            <Link to={`/coachees/${coacheeId}/edit`}>
              <Button variant="outline" className="btn-outline" data-testid="edit-coachee-btn">
                <Edit className="w-4 h-4 mr-2" />
                Editar
              </Button>
            </Link>
            <Link to={`/coachees/${coacheeId}/sessions/new`}>
              <Button className="btn-primary" data-testid="new-session-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Sesión
              </Button>
            </Link>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="glass-card p-1">
            <TabsTrigger value="profile" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <User className="w-4 h-4 mr-2" />
              Perfil
            </TabsTrigger>
            <TabsTrigger value="sessions" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <ClipboardList className="w-4 h-4 mr-2" />
              Sesiones ({sessions.length})
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Contact Info */}
              <Card className="glass-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-heading text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-900" />
                    Información de Contacto
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <p className="label-uppercase">Email</p>
                      <p className="text-slate-800 font-medium">{coachee.email || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <p className="label-uppercase">Teléfono</p>
                      <p className="text-slate-800 font-medium">{coachee.telefono || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <p className="label-uppercase">Fecha de Nacimiento</p>
                      <p className="text-slate-800 font-medium">{coachee.fecha_nacimiento || 'No especificado'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <p className="label-uppercase">Información Laboral</p>
                      <p className="text-slate-800 font-medium">{coachee.info_laboral || 'No especificado'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Objectives */}
              <Card className="glass-card">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-heading text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-900" />
                    Objetivos Generales
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-700 leading-relaxed">
                    {coachee.objetivos_generales || 'No se han definido objetivos generales.'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Observations */}
            <Card className="glass-card">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="text-heading text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-900" />
                  Observaciones del Coach
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-slate-700 leading-relaxed">
                  {coachee.observaciones || 'No hay observaciones registradas.'}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="space-y-6 animate-fade-in">
            {sessions.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="p-12 text-center">
                  <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-10 h-10 text-slate-400" />
                  </div>
                  <h3 className="text-heading text-xl text-slate-700 mb-2">
                    Sin sesiones registradas
                  </h3>
                  <p className="text-slate-500 mb-6">
                    Registra la primera sesión con {coachee.nombre}
                  </p>
                  <Link to={`/coachees/${coacheeId}/sessions/new`}>
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Nueva Sesión
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {sessions.map((session) => (
                  <Card 
                    key={session.id_sesion} 
                    className="glass-card card-hover"
                    data-testid={`session-card-${session.id_sesion}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-blue-900 to-sky-500 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0">
                            {session.numero_sesion}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800 text-lg">
                              Sesión #{session.numero_sesion}
                            </h3>
                            <p className="text-slate-500 flex items-center gap-2 mt-1">
                              <Calendar className="w-4 h-4" />
                              {session.fecha}
                            </p>
                            {session.tema_sesion && (
                              <p className="text-slate-600 mt-2">
                                <span className="font-medium">Tema:</span> {session.tema_sesion}
                              </p>
                            )}
                            {session.conclusiones_aprendizajes && (
                              <p className="text-slate-500 text-sm mt-1 line-clamp-2">
                                {session.conclusiones_aprendizajes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-[72px] sm:ml-0">
                          <Link to={`/coachees/${coacheeId}/sessions/${session.id_sesion}`}>
                            <Button variant="outline" size="sm" className="border-slate-200">
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                          </Link>
                          <Link to={`/coachees/${coacheeId}/sessions/${session.id_sesion}/edit`}>
                            <Button variant="outline" size="sm" className="border-slate-200">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </Link>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="glass-card">
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar sesión?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta acción eliminará permanentemente la sesión #{session.numero_sesion}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="btn-outline">Cancelar</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => handleDeleteSession(session.id_sesion)}
                                  className="bg-red-600 hover:bg-red-700 text-white"
                                >
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
